import { useState } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { buildUserMenuConfig } from '../../../../config/navigation/userMenuConfig';
import { getMenuIcon } from '../../../../config/navigation/menuIcons';
import useAppShellSx from '../../hooks/useAppShellSx';
import useSidebarTransition from '../../hooks/useSidebarTransition';
import SidebarTooltip from './SidebarTooltip';

function getInitials(displayName) {
  const parts = String(displayName || 'U')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return parts[0]?.slice(0, 2).toUpperCase() || 'U';
}

export default function SidebarUserFooter({
  collapsed,
  displayName,
  profileName,
  onNavigate,
  onLogout,
}) {
  const theme = useTheme();
  const { sidebar, sidebarType } = useAppShellSx();
  const { getLabelSx } = useSidebarTransition();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const userMenuItems = buildUserMenuConfig();

  const handleItemClick = (value) => {
    setAnchorEl(null);
    if (value === '/#/logout') {
      onLogout?.();
      return;
    }
    onNavigate?.(value);
  };

  const avatar = (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        bgcolor: sidebar.accent,
        color: sidebar.badgeForeground,
        ...sidebarType.avatarInitials,
      }}
    >
      {getInitials(displayName)}
    </Avatar>
  );

  return (
    <Box
      sx={{
        borderTop: `1px solid ${sidebar.border}`,
        px: collapsed ? 1 : 2,
        py: collapsed ? 1.5 : 2,
        mt: 'auto',
        transition: theme.transitions.create('padding', {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: collapsed ? 0 : 1.25,
            minWidth: 0,
            flex: 1,
            cursor: 'pointer',
          }}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          {collapsed ? (
            <SidebarTooltip title={displayName}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>{avatar}</Box>
            </SidebarTooltip>
          ) : (
            avatar
          )}

          {!collapsed && (
            <Box sx={getLabelSx(false)}>
              <Typography
                sx={{
                  ...sidebarType.userName,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </Typography>
              <Typography sx={sidebarType.userRole}>{profileName}</Typography>
            </Box>
          )}
        </Box>

        {!collapsed && (
          <IconButton
            size="small"
            aria-label="Logout"
            onClick={() => onLogout?.()}
            sx={{
              color: sidebar.textMuted,
              p: 0.75,
              '&:hover': {
                color: sidebar.text,
                bgcolor: sidebar.hover,
              },
            }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              ...sidebarType.root,
              minWidth: 180,
              borderRadius: 2,
              border: `1px solid ${sidebar.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              bgcolor: sidebar.bgElevated,
            },
          },
        }}
      >
        {userMenuItems.map((item) => {
          const Icon = getMenuIcon(item.iconKey);
          const isLogout = item.label === 'Logout';
          return (
            <MenuItem
              key={item.value}
              onClick={() => handleItemClick(item.value)}
              sx={{
                py: 1,
                color: isLogout ? 'error.main' : sidebar.textNav,
                '&:hover': {
                  bgcolor: sidebar.hover,
                  color: isLogout ? 'error.main' : sidebar.text,
                },
              }}
            >
              {Icon && (
                <ListItemIcon sx={isLogout ? { color: 'error.main' } : undefined}>
                  <Icon fontSize="small" />
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: sidebarType.navLabelNested(false),
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

SidebarUserFooter.propTypes = {
  collapsed: PropTypes.bool,
  displayName: PropTypes.string,
  profileName: PropTypes.string,
  onNavigate: PropTypes.func,
  onLogout: PropTypes.func,
};
